import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  LoadingStateType,
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { useNotifications } from '@/hooks/notifications.hook';
import { useForceGoogleWorkspaceSync } from '@/hooks/query/account-google-workspace.hooks';
import { OPERATION_LOGS_QUERY_KEY } from '@/hooks/query/operation-log.hooks';
import { useToast } from '@/hooks/useToast';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

const PING_EVENT_THRESHOLD = 10;

export const useGoogleWorkspaceSyncHandler = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const forceSync = useForceGoogleWorkspaceSync();

  const [syncState, setSyncState] = useState<LoadingStateType>(
    LoadingStatuses.IDLE,
  );

  const {
    lastMessage,
    notificationParam: accountId,
    setNotificationParam: setAccountId,
  } = useNotifications(client.googleWorkspace.syncNotifyUrl);

  const handleForceGoogleSync = async (id: string) => {
    if (!id) return;

    try {
      setSyncState(LoadingStatuses.LOADING);
      setAccountId(id);

      await forceSync.sync(id);

      toast?.info(t('toast.info'), t('job.created'));
    } catch (e) {
      setSyncState(LoadingStatuses.IDLE);
      setAccountId(undefined);
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleSyncNotification = (
    messages: { data: any; lastEventId: string } | null,
  ) => {
    if (
      !messages?.data?.event ||
      messages.data.event === 'google-workspace.account.sync.started' ||
      !['google-workspace.account.synced', 'ping'].includes(
        messages?.data?.event,
      ) ||
      (messages?.data?.event === 'ping' &&
        Number(messages?.lastEventId) <= PING_EVENT_THRESHOLD)
    ) {
      return;
    }

    if (messages.data.event === 'ping') {
      toast?.info(t('sync.status.pending'), t('sync.inProgress'));

      return;
    }

    if (messages?.data?.error && messages?.data?.error.length > 0) {
      const errorCount = Array.isArray(messages.data.error)
        ? messages.data.error.length
        : 1;

      toast?.action({
        severity: 'error',
        summary: t('toast.error'),
        detail: t('sync.issues.count', { count: errorCount }),
        actionLabel: t('generic.view.details'),
        onAction: () => {
          navigate(RedirectPaths[RedirectPathsEnum.ACCOUNT_ERRORS]());
        },
      });
    }

    if (
      messages.data.event === 'google-workspace.account.synced' &&
      (!messages?.data?.error || messages?.data?.error.length === 0)
    ) {
      toast?.success(t('toast.success'), t('account.google.synced'));
    }

    setSyncState(LoadingStatuses.IDLE);
    setAccountId(undefined);
    queryClient.invalidateQueries(OPERATION_LOGS_QUERY_KEY);
  };

  useEffect(() => {
    if (!accountId) return;

    handleSyncNotification(lastMessage);
  }, [JSON.stringify(lastMessage)]);

  return {
    handleForceGoogleSync,
    syncState,
  };
};
